import { useMemo } from "react";
import defaultPet from "$/defaultLogo.svg";
import type { NotifyDataTypes } from "./NotifySent";
import { IconCalendar } from "@/Icon/Calendar";
import { IconNotice } from "@/Icon/Notice";
import { IconInform } from "@/Icon/Inform";
import { IconGallery } from "@/Icon/Gallery";
import { IconSchool } from "@/Icon/School";

interface NoticeTimelineProps {
  items: NotifyDataTypes[];
  variant: "received" | "sent";
  onItemClick: (item: NotifyDataTypes) => void;
  emptyText?: string;
}

const DAY_LABELS = ["일", "월", "화", "수", "목", "금", "토"];

const dateKey = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "unknown";
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

const groupLabel = (key: string) => {
  if (key === "unknown") return { rel: null, abs: "날짜 미상" };
  const d = new Date(`${key}T00:00:00`);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round(
    (today.getTime() - d.getTime()) / (24 * 60 * 60 * 1000)
  );
  const abs =
    d.getFullYear() === now.getFullYear()
      ? `${d.getMonth() + 1}월 ${d.getDate()}일 ${DAY_LABELS[d.getDay()]}요일`
      : `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`;
  if (diffDays === 0) return { rel: "오늘", abs };
  if (diffDays === 1) return { rel: "어제", abs };
  return { rel: null, abs };
};

const timeLabel = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${`${d.getHours()}`.padStart(2, "0")}:${`${d.getMinutes()}`.padStart(2, "0")}`;
};

/** 알림 유형별 미니 배지 (보낸 알림장에서 유형 구분용) */
const notiTypeBadge = (cdNotiType?: string) => {
  if (!cdNotiType) return null;
  if (cdNotiType.includes("NOTI_DIARY"))
    return { label: "알림장", Icon: IconInform };
  if (cdNotiType.includes("NOTI_SCHEDULE"))
    return { label: "일정", Icon: IconCalendar };
  if (cdNotiType.includes("NOTI_ANNOUNCEMENT"))
    return { label: "공지", Icon: IconNotice };
  if (cdNotiType.includes("NOTI_PHOTO_ALBUM"))
    return { label: "사진첩", Icon: IconGallery };
  if (cdNotiType.includes("NOTI_RESERV"))
    return { label: "예약", Icon: IconCalendar };
  if (cdNotiType.includes("NOTI_ATTEND"))
    return { label: "등하원", Icon: IconSchool };
  return null;
};

/**
 * 받은/보낸 알림장 공용 타임라인 목록.
 * 날짜별 그룹 헤더(오늘/어제 + 절대 날짜 + 건수) + 좌측 레일 + 항목 카드.
 * 데이터는 이미 정렬된 순서를 그대로 유지하며 날짜 단위로만 묶는다.
 */
const NoticeTimeline = ({
  items,
  variant,
  onItemClick,
  emptyText = "알림장이 없습니다.",
}: NoticeTimelineProps) => {
  const groups = useMemo(() => {
    const result: Array<{ key: string; items: NotifyDataTypes[] }> = [];
    for (const item of items) {
      const key = dateKey(item.dtSend);
      const last = result[result.length - 1];
      if (last && last.key === key) {
        last.items.push(item);
      } else {
        result.push({ key, items: [item] });
      }
    }
    return result;
  }, [items]);

  if (items.length === 0) {
    return (
      <div className="rounded-xl bg-white p-6 text-center text-sm text-gray">
        {emptyText}
      </div>
    );
  }

  return (
    <div>
      {groups.map((group) => {
        const label = groupLabel(group.key);
        return (
          <div key={group.key}>
            {/* 날짜 그룹 헤더 */}
            <div className="flex items-center gap-2 py-2">
              {label.rel && (
                <span className="text-[13px] font-bold">{label.rel}</span>
              )}
              <span
                className={
                  label.rel
                    ? "text-[11px] text-gray"
                    : "text-[13px] font-bold"
                }
              >
                {label.abs}
              </span>
              <span className="h-px flex-1 bg-[#E5E5E5]" />
              <span className="text-[11px] text-gray">
                {group.items.length}건
              </span>
            </div>

            {/* 좌측 레일 + 카드 */}
            <div className="relative mb-2 pl-[18px]">
              <span className="absolute bottom-2 left-[5px] top-2 w-[2px] rounded-full bg-[#E5E5E5]" />
              {group.items.map((item) => {
                const highlight =
                  item.businessNewReplyYn === "Y" || item.readYn === "N";
                // 수신 단위가 반려견이므로 보낸 목록도 팻 이름 기준으로 표기
                const baseName =
                  variant === "sent"
                    ? item.petName || item.recipientName
                    : item.petName;
                // 다중 발송 알림장은 목록 쿼리가 수신자 1명만 내려주므로 '외 N'으로 보정
                const extraCount =
                  variant === "sent" && item.recipientCount
                    ? item.recipientCount - 1
                    : 0;
                const name =
                  extraCount > 0 ? `${baseName} 외 ${extraCount}` : baseName;
                const typeBadge =
                  variant === "sent" ? notiTypeBadge(item.cdNotiType) : null;
                // 일정공지/공지사항은 특정 반려견 대상이 아니므로 수신자 썸네일 대신 유형 아이콘 노출
                const isIconAvatar =
                  variant === "sent" &&
                  !!typeBadge &&
                  (item.cdNotiType?.includes("NOTI_SCHEDULE") ||
                    item.cdNotiType?.includes("NOTI_ANNOUNCEMENT"));
                return (
                  <div
                    key={item.notiRecvSeq}
                    data-noti-seq={item.notiRecvSeq}
                    className="relative mb-2"
                  >
                    <span
                      className={`absolute -left-[17px] top-1/2 h-2 w-2 -translate-y-1/2 rounded-full ${
                        highlight ? "bg-yellow" : "bg-[#CFCFCF]"
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => onItemClick(item)}
                      className="flex w-full items-center gap-2.5 rounded-xl bg-white p-3 text-left"
                    >
                      <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full border border-[#F0F0F0] bg-[#F5F5F5]">
                        {isIconAvatar && typeBadge ? (
                          <span
                            title={typeBadge.label}
                            aria-label={typeBadge.label}
                            className="flex h-full w-full items-center justify-center bg-[#FFF3E5] [&_svg]:h-5 [&_svg]:w-5"
                          >
                            <typeBadge.Icon active />
                          </span>
                        ) : (
                          <img
                            src={item.profileImgUrl || defaultPet}
                            alt={name}
                            className="h-full w-full object-cover"
                          />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          {typeBadge && !isIconAvatar && (
                            <span
                              title={typeBadge.label}
                              aria-label={typeBadge.label}
                              className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#F5F5F5] [&_svg]:h-3.5 [&_svg]:w-3.5"
                            >
                              <typeBadge.Icon />
                            </span>
                          )}
                          <span className="truncate text-sm font-bold">
                            {name}
                          </span>
                          {item.businessNewReplyYn === "Y" && (
                            <span className="shrink-0 rounded-full bg-[#FDECEC] px-1.5 py-0.5 text-[10px] font-bold text-[#E24B4A]">
                              새 댓글
                            </span>
                          )}
                          {variant === "received" && item.readYn === "N" && (
                            <span className="shrink-0 rounded-full bg-[#FFF3E5] px-1.5 py-0.5 text-[10px] font-bold text-yellow">
                              안읽음
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 truncate text-xs text-gray">
                          {item.notiTitle}
                        </div>
                      </div>
                      <span className="shrink-0 text-[11px] text-gray">
                        {timeLabel(item.dtSend)}
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default NoticeTimeline;
